import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAppointmentsByClinicianId,
  fetchAppointmentsByClinicianAndDay,
  fetchAppointmentById,
  fetchAppointmentsByClinicianAndDateRange,
} from "../../../store/thunks/appointmentsThunk";
import { fetchTodos } from "../../../store/thunks/todoThunk";
import { logInfo, logError } from "../../../utils/logger";
import { Tab } from "@headlessui/react";
import {
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  PhoneIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from "@heroicons/react/24/outline";
import ClinicianTodoItem from "../../../applicationUi/components/ClinicianTodoItem";
import ClinicianDetailsCard from "../../../applicationUi/components/ClinicianDetailsCard";
import AppointmentsSection from "../../../applicationUi/components/appointments/AppointmentsSection";
import { format, differenceInMinutes } from "date-fns";
import AppointmentSummary from "../../../applicationUi/components/appointments/AppointmentSummary";
import { setSelectedAppointment } from "../../../store/slices/appointmentsSlice";

export default function ClinicianDashboard({
  clinician,
  onAppointmentClick,
  onReschedule,
  onWideSidePanelClose,
}) {
  const dispatch = useDispatch();
  const appointments = useSelector(
    (state) => state.appointments.appointments || []
  );
  const todosState = useSelector((state) => state.todos);
  const todos = todosState.todos || {};
  const user = useSelector((state) => state.auth.user || []);
  const [activeTab, setActiveTab] = useState(0);
  const [dateFilter, setDateFilter] = useState("day");
  const [viewType, setViewType] = useState("timeline");
  const [refresh, setRefresh] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [todaysAppointments, setTodaysAppointments] = useState([]);
  const [sortOrder, setSortOrder] = useState("newest");

  useEffect(() => {
    if (clinician?._id) {
      console.log("Fetching appointments for clinician:", clinician._id);

      // Get today's date at midnight
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Get tomorrow's date at midnight
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Get next week's date
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);

      // Determine which date range to fetch based on the dateFilter
      let startDate, endDate;

      switch (dateFilter) {
        case "day":
          startDate = today;
          endDate = tomorrow;
          break;
        case "week":
          startDate = today;
          endDate = nextWeek;
          break;
        case "month":
          startDate = today;
          endDate = new Date(today);
          endDate.setMonth(endDate.getMonth() + 1);
          break;
        default:
          startDate = today;
          endDate = tomorrow;
      }

      console.log("Fetching appointments for date range:", {
        startDate,
        endDate,
      });

      if (dateFilter === "day") {
        // Use the single day API
        const dateString = today.toISOString().split("T")[0];
        console.log("Fetching day appointments with date:", dateString);
        dispatch(
          fetchAppointmentsByClinicianAndDay({
            clinicianId: clinician._id,
            date: dateString,
          })
        )
          .unwrap()
          .then((response) => {
            console.log("Day appointments fetched successfully:", response);
            logInfo("Successfully fetched appointments for clinician", {
              componentName: "ClinicianDashboard",
              action: "fetchAppointmentsByClinicianAndDay",
            });
          })
          .catch((error) => {
            console.error("Failed to fetch appointments:", error);
            logError("Failed to fetch appointments:", {
              componentName: "ClinicianDashboard",
              action: "fetchAppointmentsByClinicianAndDay",
              error: error.message,
            });
          });
      } else {
        // For week/month view, use the date range endpoint
        dispatch(
          fetchAppointmentsByClinicianAndDateRange({
            clinicianId: clinician._id,
            startDate,
            endDate,
          })
        )
          .unwrap()
          .then((response) => {
            console.log("Date range appointments fetched successfully:", response);
            logInfo("Successfully fetched appointments for date range", {
              componentName: "ClinicianDashboard",
              action: "fetchAppointmentsByClinicianAndDateRange",
            });
          })
          .catch((error) => {
            console.error("Failed to fetch appointments:", error);
            logError("Failed to fetch appointments:", {
              componentName: "ClinicianDashboard",
              action: "fetchAppointmentsByClinicianAndDateRange",
              error: error.message,
            });
          });
      }

      // Fetch todos for this specific clinician
      dispatch(
        fetchTodos({
          userRole: "clinician",
          clinicianId: clinician._id,
        })
      )
        .unwrap()
        .then((response) => {
          console.log("Todos fetched successfully:", response);
          logInfo("Successfully fetched todos for clinician", {
            componentName: "ClinicianDashboard",
            action: "fetchTodos",
            userRole: "clinician",
          });
        })
        .catch((error) => {
          console.error("Failed to fetch todos:", error);
          logError("Failed to fetch todos:", {
            componentName: "ClinicianDashboard",
            action: "fetchTodos",
            userRole: "clinician",
            error: error.message,
          });
        });
    }
  }, [dispatch, clinician, refresh, dateFilter]);

  // Fetch today's appointments separately
  useEffect(() => {
    if (clinician?._id) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      console.log(
        "Fetching today's appointments for clinician:",
        clinician._id
      );
      dispatch(
        fetchAppointmentsByClinicianAndDay({
          clinicianId: clinician._id,
          date: today.toISOString().split("T")[0],
        })
      )
        .unwrap()
        .then((response) => {
          console.log("Today's appointments fetched successfully:", response);
          setTodaysAppointments(response || []);
        })
        .catch((error) => {
          console.error("Failed to fetch today's appointments:", error);
        });
    }
  }, [dispatch, clinician]);

  // Debug logging for appointments
  useEffect(() => {
    console.log("Current appointments:", appointments);
    console.log("Today's appointments:", todaysAppointments);
  }, [appointments, todaysAppointments]);

  const getFilteredAppointments = () => {
    console.log("Raw appointments from Redux state:", appointments);
    console.log("Filtering appointments:", {
      total: appointments.length,
      dateFilter,
      appointments,
    });

    if (!appointments.length) {
      console.log("No appointments to filter");
      return [];
    }

    // Create a copy of the appointments array before sorting
    const appointmentsCopy = [...appointments];

    // Filter appointments based on the selected date filter
    const filteredAppointments = appointmentsCopy.filter((appointment) => {
      const appointmentDate = new Date(appointment.start);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);

      const lastWeek = new Date(today);
      lastWeek.setDate(lastWeek.getDate() - 7);

      switch (dateFilter) {
        case "day":
          // Only include appointments from today (not tomorrow)
          return appointmentDate >= today && appointmentDate < tomorrow;
        case "week":
          // Include appointments from today through the next 7 days
          return appointmentDate >= today && appointmentDate <= nextWeek;
        case "month":
          // Include appointments from today through the next 30 days
          const nextMonth = new Date(today);
          nextMonth.setMonth(nextMonth.getMonth() + 1);
          return appointmentDate >= today && appointmentDate <= nextMonth;
        case "past":
          // Include appointments from the past week
          return appointmentDate >= lastWeek && appointmentDate < today;
        default:
          return true;
      }
    });

    // Sort appointments by start time
    const finalFilteredAppointments = filteredAppointments.sort(
      (a, b) => new Date(a.start) - new Date(b.start)
    );
    console.log("Final filtered appointments being passed to AppointmentsSection:", finalFilteredAppointments);
    return finalFilteredAppointments;
  };

  // Get today's appointments for the card display
  const getTodaysAppointments = () => {
    return todaysAppointments;
  };

  const getTodoCount = (type) => {
    const categoryMap = {
      treat: "Treat",
      soap: "Soap",
      approve: "Approve SOAP",
    };

    const category = categoryMap[type];
    if (!category || !todos[category]) return 0;
    return todos[category].length;
  };

  const getTodoItems = (type) => {
    const categoryMap = {
      treat: "Treat",
      soap: "Soap",
      approve: "Approve SOAP",
    };

    const category = categoryMap[type];
    if (!category || !todos[category]) return [];

    let items = todos[category].map((todo) => ({
      ...todo,
      type: category,
    }));

    // Sort the items based on sortOrder
    items.sort((a, b) => {
      const dateA = new Date(a.start);
      const dateB = new Date(b.start);
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

    return items;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Not Confirmed":
        return "bg-yellow-100 text-yellow-800";
      case "Confirmed":
        return "bg-green-100 text-green-800";
      case "Cancelled":
        return "bg-red-100 text-red-800";
      case "Completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getGroupedAppointments = (appointments) => {
    const grouped = {};

    // Group by day for week and month views
    if (dateFilter === "week" || dateFilter === "month") {
      appointments.forEach((appointment) => {
        const date = format(new Date(appointment.start), "EEEE, MMMM d"); // e.g. "Monday, January 15"
        const time = format(new Date(appointment.start), "h:mm a");

        if (!grouped[date]) {
          grouped[date] = {};
        }
        if (!grouped[date][time]) {
          grouped[date][time] = [];
        }
        grouped[date][time].push(appointment);
      });
    } else {
      // Original grouping by time only for day view
      appointments.forEach((appointment) => {
        const time = format(new Date(appointment.start), "h:mm a");
        if (!grouped[time]) {
          grouped[time] = [];
        }
        grouped[time].push(appointment);
      });
    }
    return grouped;
  };

  const handleAppointmentClick = (appointment) => {
    console.log("Opening appointment:", appointment);
    onAppointmentClick(appointment);
  };

  const handleTodoClick = async (todo) => {
    console.log("Todo clicked:", todo);

    // The todo object itself contains the full appointment data
    if (todo) {
      onAppointmentClick(todo);
    }
  };

  const handleCloseSummary = () => {
    console.log("Closing appointment summary");
    setSelectedAppointment(null);
    setIsSummaryOpen(false);
  };

  // This will log every render, right before the JSX is returned
  useEffect(() => {
    console.log("Appointments passed to AppointmentsSection:", getFilteredAppointments());
  }, [appointments, dateFilter, viewType]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Top Section with Clinician Details */}
        {/* <div className="flex justify-end">
          <div className="w-1/3">
            <ClinicianDetailsCard clinician={clinician} />
          </div>
        </div> */}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-indigo-50 rounded-sm shadow p-6 border border-indigo-100">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-indigo-500">
                <ClipboardDocumentCheckIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-indigo-600">To Treat</p>
                <p className="text-2xl font-semibold text-indigo-700">
                  {getTodoCount("treat")}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-green-50 rounded-sm shadow p-6 border border-green-100">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-500">
                <DocumentTextIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-green-600">SOAP Notes</p>
                <p className="text-2xl font-semibold text-green-700">
                  {getTodoCount("soap")}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-amber-50 rounded-sm shadow p-6 border border-amber-100">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-amber-500">
                <ClipboardDocumentListIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-amber-600">To Approve</p>
                <p className="text-2xl font-semibold text-amber-700">
                  {getTodoCount("approve")}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 rounded-sm shadow p-6 border border-blue-100">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-500">
                <ClockIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-blue-600">
                  Today's Appointments
                </p>
                <p className="text-2xl font-semibold text-blue-700">
                  {getTodaysAppointments().length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Todos Section */}
          <div className="bg-white rounded-sm shadow">
            <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
              <Tab.List className="flex border-b border-gray-200">
                <Tab
                  className={({ selected }) =>
                    `flex-1 py-4 px-4 text-center font-medium text-sm focus:outline-none
                  ${
                    selected
                      ? "text-indigo-600 border-b-2 border-indigo-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`
                  }>
                  To Treat ({getTodoCount("treat")})
                </Tab>
                <Tab
                  className={({ selected }) =>
                    `flex-1 py-4 px-4 text-center font-medium text-sm focus:outline-none
                  ${
                    selected
                      ? "text-green-600 border-b-2 border-green-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`
                  }>
                  SOAP Notes ({getTodoCount("soap")})
                </Tab>
                <Tab
                  className={({ selected }) =>
                    `flex-1 py-4 px-4 text-center font-medium text-sm focus:outline-none
                  ${
                    selected
                      ? "text-amber-600 border-b-2 border-amber-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`
                  }>
                  To Approve ({getTodoCount("approve")})
                </Tab>
                <div className="flex items-center px-4 border-l border-gray-200">
                  <button
                    onClick={() =>
                      setSortOrder(sortOrder === "newest" ? "oldest" : "newest")
                    }
                    className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-sm"
                    title={
                      sortOrder === "newest"
                        ? "Sort by oldest first"
                        : "Sort by newest first"
                    }>
                    {sortOrder === "newest" ? (
                      <ArrowDownIcon className="h-5 w-5" />
                    ) : (
                      <ArrowUpIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </Tab.List>
              <Tab.Panels className="p-4">
                <Tab.Panel>
                  <div className="space-y-4">
                    {getTodoItems("treat").map((todo) => (
                      <ClinicianTodoItem
                        key={todo._id}
                        todo={todo}
                        onClick={handleTodoClick}
                      />
                    ))}
                  </div>
                </Tab.Panel>
                <Tab.Panel>
                  <div className="space-y-4">
                    {getTodoItems("soap").map((todo) => (
                      <ClinicianTodoItem
                        key={todo._id}
                        todo={todo}
                        onClick={handleTodoClick}
                      />
                    ))}
                  </div>
                </Tab.Panel>
                <Tab.Panel>
                  <div className="space-y-4">
                    {getTodoItems("approve").map((todo) => (
                      <ClinicianTodoItem
                        key={todo._id}
                        todo={todo}
                        onClick={handleTodoClick}
                      />
                    ))}
                  </div>
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </div>

          {/* Appointments Section */}
          <AppointmentsSection
            appointments={getFilteredAppointments()}
            viewType={viewType}
            dateFilter={dateFilter}
            onAppointmentClick={handleAppointmentClick}
            onViewTypeChange={setViewType}
            onDateFilterChange={setDateFilter}
            getStatusBadgeClass={getStatusBadgeClass}
          />
        </div>

        {/* Appointment Summary Modal */}
        {/* {selectedAppointment && (
          <AppointmentSummary
            open={isSummaryOpen}
            appointment={selectedAppointment}
            onClose={handleCloseSummary}
          />
        )} */}
      </div>
    </div>
  );
}
